import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { CopyIcon, DownloadIcon, RocketIcon, SaveIcon, XIcon } from 'lucide-react';
import { getWidgetStores, type WidgetFullModeProps } from '@/entities/widget';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { ToggleViewSwitch } from '@/shared/ui/ToggleViewSwitch';
import { usePublishTemplate } from '@/shared/hooks/usePublishTemplate';
import { notificationService } from '@/lib/services/notification.service';
import type { EmailData, EmailFlowWidgetProps } from '../model/email-flow-widget.types';
import { useArtifactSave, useEmailActions } from '../model/hooks';
import { CodeView } from './components/CodeView';
import { PreviewView } from './components/PreviewView';
import { PublishTemplateModal } from './components/PublishTemplateModal';
import { useTranslation } from 'react-i18next';

export function EmailFlowWidget(props: WidgetFullModeProps<EmailFlowWidgetProps>) {
  const { t } = useTranslation('contentTest');

  const {
    data,
    widgetSchemaKey,
    enableCopyEditing,
    enableImageEditing,
    useKnowledgeBaseImagesHook,
    enablePublish,
    readOnly,
  } = props;
  const [showCodeView, setShowCodeView] = useState<boolean>(false);

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/widget*` modules; tests stub
  // `getWidgetStores()` at the seam.
  const { widgetStore } = getWidgetStores();
  const { widget } = widgetStore.useWidgetPanel();
  const agentId = widget?.agentName ?? '';
  const widgetKey = widgetSchemaKey ?? 'email-flow-widget:v1';
  const versionConfig = widgetStore.useWidgetVersions(agentId);

  const validData = useMemo(() => {
    return data && typeof data === 'object' && 'subjectLine' in data ? data : null;
  }, [data]);

  const memoizedData = useMemo(() => {
    return {
      htmlContent: validData?.htmlContent ?? '',
      preheaderText: validData?.preheaderText ?? '',
      subjectLine: validData?.subjectLine ?? '',
    };
  }, [validData]);

  const [emailData, setEmailData] = useState<EmailData>(memoizedData);

  useEffect(() => {
    setEmailData(memoizedData);
  }, [memoizedData]);

  const { save, hasUnsavedChanges, isPending } = useArtifactSave({
    emailData,
    artifactData: memoizedData,
    agentId,
    widgetSchemaKey: widgetKey,
  });

  const handleCopyEditChange = useCallback((newHtml: string) => {
    setEmailData((prev) => ({ ...prev, htmlContent: newHtml }));
  }, []);

  const codeView = useMemo(
    () => (
      <div
        className="w-full flex-1 overflow-hidden"
        style={{ display: showCodeView ? 'block' : 'none' }}
      >
        <div className="w-full h-full overflow-auto">
          <CodeView
            emailData={emailData}
            onChange={setEmailData}
            agentId={agentId}
            widgetSchemaKey={widgetSchemaKey}
            hideSaveButton={enableCopyEditing}
          />
        </div>
      </div>
    ),
    [emailData, showCodeView, agentId, widgetSchemaKey, enableCopyEditing],
  );

  const previewView = useMemo(
    () => (
      <div
        className="w-full h-auto min-h-0 flex-1"
        style={{ display: showCodeView ? 'none' : 'block' }}
      >
        <PreviewView
          html={emailData.htmlContent}
          subjectLine={emailData.subjectLine}
          preheaderText={emailData.preheaderText}
          enableCopyEditing={enableCopyEditing}
          enableImageEditing={enableImageEditing}
          useKnowledgeBaseImagesHook={useKnowledgeBaseImagesHook}
          onHtmlChange={handleCopyEditChange}
        />
      </div>
    ),
    [
      emailData,
      showCodeView,
      enableCopyEditing,
      enableImageEditing,
      useKnowledgeBaseImagesHook,
      handleCopyEditChange,
    ],
  );

  const { handleCopyHtml, handleExportHtml, handleClose } = useEmailActions(emailData.htmlContent);

  // Publish-to-pipelines (templates agent only). The query is gated on
  // `enablePublish` so the plain email-flow agent never fires it; `showPublish`
  // is true only when ≥1 pipeline is registered for this workspace + agent.
  const { showPublish, isPublishing, publish, outcome, resetPublish } = usePublishTemplate(
    agentId,
    Boolean(enablePublish),
  );
  const showPublishButton = Boolean(enablePublish) && showPublish;
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  // Clicking Publish fires the publish immediately and opens the feedback modal (the asset name is
  // derived, not prompted). Guards an empty artifact first.
  const openPublishModal = useCallback(() => {
    if (!emailData.htmlContent) {
      notificationService.showError(t('emailFlowWidget.nothingToPublish'));
      return;
    }
    resetPublish();
    setPublishModalOpen(true);
    publish({
      html: emailData.htmlContent,
      subjectLine: emailData.subjectLine,
      preheaderText: emailData.preheaderText,
      // Derived name (no prompt); the neuron prefers the brief's Brief_Name and falls back to this.
      // eslint-disable-next-line i18next/no-literal-string -- asset name in the publish payload
      assetName: emailData.subjectLine || 'email',
      // The exact ".campaign" used to create this email, carried on the artifact.
      campaignId: validData?.campaignId ?? undefined,
    });
  }, [emailData, publish, resetPublish, t, validData]);

  const closePublishModal = useCallback(() => {
    setPublishModalOpen(false);
    resetPublish();
  }, [resetPublish]);

  // Wrapped in useMemo so the reference is stable when its inputs don't
  // change — without this, the conditional below would mint a fresh array
  // each render and force the headerConfig useMemo to invalidate every
  // time (`react-hooks/exhaustive-deps`).
  const saveButtonGroup = useMemo(
    () =>
      enableCopyEditing
        ? [
            [
              {
                type: 'button' as const,
                label: t('emailFlowWidget.save'),
                icon: isPending ? <Spinner aria-hidden /> : <SaveIcon />,
                onClick: () => void save(),
                variant: 'contained' as const,
                disabled: !hasUnsavedChanges || isPending,
                tooltip: t('emailFlowWidget.saveTemplate'),
              },
            ],
          ]
        : [],
    [enableCopyEditing, isPending, save, hasUnsavedChanges, t],
  );

  const headerConfig = useMemo(
    () => ({
      chip: { label: t('emailFlowWidget.email'), color: 'red' as const },
      versionConfig,
      actionGroups: [
        // The HTML/preview toggle and the close-section X are editing/
        // navigation controls with no meaning in the read-only Library preview
        // (there's no section to close, and the rendered email is the point) —
        // drop them there. Copy + download stay; they're read affordances.
        ...(readOnly
          ? []
          : [
              [
                // Publish sits first in the toggle's group, so the header renders
                // it immediately LEFT of the code|preview toggle (no divider).
                // Rendered as a custom component so it reuses the app's canonical
                // Publish button (monks-ui Button + Spinner + RocketIcon), matching
                // the BriefTemplateBuilder "Publish" affordance.
                ...(showPublishButton
                  ? [
                      {
                        type: 'custom' as const,
                        component: (
                          <Button
                            variant="default"
                            size="sm"
                            className="mr-2"
                            onClick={openPublishModal}
                            disabled={isPublishing}
                            title={t('emailFlowWidget.publishTemplateToRegisteredPipelines')}
                          >
                            {isPublishing ? <Spinner /> : <RocketIcon className="size-3.5" />}
                            {isPublishing
                              ? t('emailFlowWidget.publishing')
                              : t('emailFlowWidget.publish')}
                          </Button>
                        ),
                      },
                    ]
                  : []),
                {
                  type: 'custom' as const,
                  component: (
                    <ToggleViewSwitch
                      showCodeView={showCodeView}
                      onChange={() => setShowCodeView(!showCodeView)}
                    />
                  ),
                },
              ],
            ]),
        [
          {
            type: 'icon' as const,
            icon: <CopyIcon />,
            onClick: () => void handleCopyHtml(),
            tooltip: t('emailFlowWidget.copyHtml'),
          },
          {
            type: 'icon' as const,
            icon: <DownloadIcon />,
            onClick: handleExportHtml,
            tooltip: t('emailFlowWidget.downloadHtml'),
          },
        ],
        ...saveButtonGroup,
        ...(readOnly
          ? []
          : [
              [
                {
                  type: 'icon' as const,
                  icon: <XIcon />,
                  onClick: handleClose,
                  tooltip: t('emailFlowWidget.closeEmailSection'),
                },
              ],
            ]),
      ],
    }),
    [
      readOnly,
      showCodeView,
      handleCopyHtml,
      handleExportHtml,
      handleClose,
      versionConfig,
      saveButtonGroup,
      showPublishButton,
      isPublishing,
      openPublishModal,
      t,
    ],
  );

  if (!data) return <div>{t('emailFlowWidget.noDataProvidedForEmailFlowWidget')}</div>;
  if (!validData) {
    return <div>{t('emailFlowWidget.noEmailFlowDataAvailable')}</div>;
  }

  return (
    <SidePanel headerConfig={headerConfig}>
      <div className="flex flex-col w-full h-auto overflow-visible py-2">
        {previewView}
        {codeView}
      </div>
      {showPublishButton && (
        <PublishTemplateModal
          open={publishModalOpen}
          outcome={outcome}
          onClose={closePublishModal}
        />
      )}
    </SidePanel>
  );
}
